/** 完整对话: the whole conversation of a practice, its lines playable. */

import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { aiColors } from '../../../ai-suite/theme';
import { SubtitledHeader } from '../../components/SubtitledHeader';
import { formatName, levelName } from '../../copy';
import { usePracticeReport } from '../../practice/functions/usePracticeReport';
import { useTranscriptPlayback } from '../../practice/functions/useTranscriptPlayback';
import { TranscriptView } from '../../practice/report/TranscriptView';
import { StarRow } from '../../practice/report/components/StarRow';

export const CoachingTranscriptScreen = wrapNavigatorScreen(
  ({ attemptId }: { attemptId: string }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const { state, reload } = usePracticeReport(attemptId);
    const replay = useTranscriptPlayback(attemptId);
    const [playing] = useBehaviorSubject(replay.playing$);
    const [voices] = useBehaviorSubject(replay.voices$);
    const [traineePlaying] = useBehaviorSubject(replay.traineePlaying$);
    // A line still playing stops when another screen opens over this one.
    useFocusEffect(useCallback(() => () => replay.stop(), [replay]));
    const report = state.kind === 'ready' ? state.report : null;
    return (
      <View style={{ flex: 1, backgroundColor: aiColors.background }}>
        <SubtitledHeader
          title="完整对话"
          subtitle={
            report
              ? `${report.title} · ${formatName(report.format)} · ${levelName(
                  report.difficulty,
                )}`
              : undefined
          }
          onBack={() => navigation.goBack()}
          trailing={
            report?.stars != null ? (
              <StarRow stars={report.stars} size={rpx(14)} />
            ) : null
          }
        />
        {state.kind === 'reviewing' ? (
          <ActivityIndicator
            color={aiColors.primary}
            style={{ marginTop: rpx(48) }}
          />
        ) : state.kind === 'failed' ? (
          <EmptyView message="对话暂时没加载出来" onRetry={reload} />
        ) : (
          <TranscriptView
            report={state.report}
            playing={playing}
            voices={voices}
            position$={replay.position$}
            onPlay={turn => void replay.play(turn)}
            traineePlaying={traineePlaying}
            onPlayTrainee={replay.playTrainee}
            onRetryMoment={turn =>
              navigation.push('practice', { attemptId, momentTurn: turn })
            }
          />
        )}
      </View>
    );
  },
  { title: '完整对话', headerShown: false },
);
